import { CustomFieldDefinition, CustomFieldsMapper, LocationMapper } from '@nx-smartmonkey/shared/domain';
import { Client } from '../../domain/client/Client';
import { ClientDTO } from '../../domain/client/ClientDTO';
import { ClientFlat } from '../../domain/client/ClientFlat';
import { ClientFlatDTO } from '../../domain/client/ClientFlatDTO';
import { ClientRepositoryDTO } from '../../domain/client/ClientRepositoryDTO';
import { Stop } from '../../domain/stop/Stop';

export class ClientMapper {
  static toDomain(props: ClientDTO, customFieldDefinitions: CustomFieldDefinition[]): Client {
    const {
      id,
      organization_id,
      project_id,
      label,
      external_id,
      location,
      location_details,
      comments,
      phone,
      email,
      url,
      reference_person,
      default_duration,
      default_requires,
      default_time_windows,
      default_volume,
      default_weight,
      custom_fields,
      default_max_delivery_time,
      default_cluster,
      deleted,
      created_by,
      created_at,
      updated_at,
      deleted_at,
    } = props;

    const locationDomain = location ? LocationMapper.toDomain(location) : undefined;

    return Client.create(
      {
        organization_id,
        project_id,
        label,
        external_id,
        location: locationDomain,
        location_details,
        comments,
        phone,
        email,
        url,
        reference_person,
        default_duration,
        default_requires,
        default_time_windows,
        default_volume,
        default_weight,
        custom_fields: custom_fields ? CustomFieldsMapper.toDomain(custom_fields, customFieldDefinitions) : undefined,
        default_max_delivery_time,
        default_cluster,
        deleted,
        created_by,
        created_at,
        updated_at,
        deleted_at,
      },
      id
    );
  }

  static toFlatDomain(props: ClientFlatDTO): ClientFlat {
    const { id, lat, lng } = props;

    return ClientFlat.create(
      {
        lat,
        lng,
      },
      id
    );
  }

  static toDTO(client: Client): ClientDTO {
    return {
      id: client.id,
      organization_id: client.organization_id,
      project_id: client.project_id,
      label: client.label,
      external_id: client.external_id,
      location: client.location ? LocationMapper.toDTO(client.location) : undefined,
      location_details: client.location_details,
      comments: client.comments,
      phone: client.phone,
      email: client.email,
      url: client.url,
      reference_person: client.reference_person,
      default_duration: client.default_duration,
      default_requires: client.default_requires,
      default_time_windows: client.default_time_windows,
      default_volume: client.default_volume,
      default_weight: client.default_weight,
      custom_fields: client.custom_fields,
      default_max_delivery_time: client.default_max_delivery_time,
      default_cluster: client.default_cluster,
      deleted: client.deleted,
      created_by: client.created_by,
      created_at: client.created_at,
      updated_at: client.updated_at,
      deleted_at: client.deleted_at,
    };
  }

  static toFlatDTO(flatClient: ClientFlat): ClientFlatDTO {
    const { id, lat, lng } = flatClient;
    return {
      id,
      lat,
      lng,
    };
  }

  static toStop(client: Client): Stop {
    const {
      id,
      project_id,
      external_id,
      label,
      location,
      comments,
      phone,
      email,
      url,
      reference_person,
      location_details,
      default_duration,
      default_requires,
      default_volume,
      default_weight,
      default_time_windows,
      custom_fields,
      default_max_delivery_time,
      default_cluster,
    } = client;

    return Stop.create({
      label,
      type: `delivery`,
      status: `pending`,
      project_id,
      location,
      comments,
      phone,
      email,
      url,
      reference_person,
      client_id: id,
      client_external_id: external_id,
      location_details: location_details,
      duration: default_duration,
      requires: default_requires,
      volume: default_volume,
      weight: default_weight,
      time_windows: default_time_windows,
      custom_fields: custom_fields,
      max_delivery_time: default_max_delivery_time,
      cluster: default_cluster,
      supervisors: [],
    });
  }

  static fromStop(stop: Stop): Client {
    const {
      label,
      project_id,
      location,
      comments,
      phone,
      email,
      url,
      location_details,
      client_external_id,
      reference_person,
      requires,
      volume,
      weight,
      time_windows,
      max_delivery_time,
      cluster,
    } = stop;

    const client = Client.create({
      label,
      project_id,
      location: location ?? undefined,
      location_details,
      comments,
      phone,
      email,
      url,
      reference_person,
      external_id: client_external_id,
      default_requires: requires,
      default_volume: volume,
      default_weight: weight,
      default_time_windows: time_windows,
      default_max_delivery_time: max_delivery_time,
      default_cluster: cluster,
    });

    return client;
  }

  static toRepository(client: Client): ClientRepositoryDTO {
    const {
      external_id,
      location,
      location_details,
      label,
      comments,
      phone,
      email,
      url,
      reference_person,
      default_duration,
      default_requires,
      default_time_windows,
      default_volume,
      default_weight,
      custom_fields,
      default_max_delivery_time,
      default_cluster,
    } = client;
    return {
      external_id,
      ...(location !== undefined ? { location: location !== null ? LocationMapper.toDTO(location) : null } : {}),
      location_details,
      label,
      comments,
      phone,
      email,
      url,
      reference_person,
      default_duration,
      default_requires,
      default_time_windows,
      default_volume,
      default_weight,
      custom_fields: custom_fields ? CustomFieldsMapper.toRepository(custom_fields) : undefined,
      default_max_delivery_time,
      default_cluster,
    };
  }
}
